import styles from "./assets/css/BrandsSection.module.css";
import useFetch from "../../Hooks/useFetch";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import gloveMobile from "./assets/img/glove_mobile.webp";
import gloveMedium from "./assets/img/glove_medium.webp";
import gloveLarge from "./assets/img/glove_large.webp";

import { CSSTransition } from "react-transition-group";

const BrandsSection = () => {
  const [activeBrand, setActiveBrand] = useState(null);
  const [sortedBrands, setSortedBrands] = useState(null);
  const [currentBrandIndex, setCurrentBrandIndex] = useState(null);
  const { data: brands, loading, error } = useFetch("premium_brands");
  const videoContainerRef = useRef(null);
  const textContainerRef = useRef(null);
  const videoPlayerRef = useRef(null);
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoPlay, setVideoPlay] = useState(true);

  const [videoPlayerIn, setVideoPlayerIn] = useState(false);

  const [queryParam] = useSearchParams();

  const brandName = queryParam.get("brand");

  useEffect(() => {
    if (!videoPlayerRef.current) return;
    if (videoPlay) {
      videoPlayerRef.current.play();
    } else {
      videoPlayerRef.current.pause();
    }
  }, [videoPlay]);

  useEffect(() => {
    if (!brands) return;
    const sorted = brands
      .filter((brand) => brand.acf.premium_brands.show_on_brands_page)
      ?.toSorted(
        (a, b) => a.acf.premium_brands.order - b.acf.premium_brands.order
      );
    setSortedBrands(sorted);
  }, [brands]);

  useEffect(() => {
    setTimeout(() => {
      setVideoPlayerIn(true);
    }, 1000);
  }, []);

  useEffect(() => {
    if (!sortedBrands) return;
    if (brandName) {
      setActiveBrand(
        sortedBrands.filter((brand) => brand.title.rendered === brandName)[0].id
      );
    } else {
      setActiveBrand(sortedBrands[0].id);
    }
  }, [sortedBrands, brandName]);

  useEffect(() => {
    if (!sortedBrands) return;
    setCurrentBrandIndex(
      sortedBrands.findIndex((item) => item.id === activeBrand)
    );
  }, [activeBrand, sortedBrands]);

  const handleChangeActiveBrand = (brandId) => {
    setVideoPlayerIn(false);
    setTimeout(() => {
      setActiveBrand(brandId);
      setVideoPlayerIn(true);
    }, 850);
  };

  useEffect(() => {
    if (!activeBrand) return;
    setVideoPlayerIn(true);
  }, [activeBrand]);

  const togglePlay = () => {
    setVideoPlay((current) => !current);
  };

  const toggleMute = () => {
    setVideoMuted(!videoPlayerRef.current.muted);
  };

  const fullscreenClick = () => {
    if (videoPlayerRef.current.requestFullscreen) {
      videoPlayerRef.current.requestFullscreen();
    } else if (videoPlayerRef.current.mozRequestFullScreen) {
      videoPlayerRef.current.mozRequestFullScreen();
    } else if (videoPlayerRef.current.webkitRequestFullscreen) {
      videoPlayerRef.current.webkitRequestFullscreen();
    }
  };

  return loading ? (
    <section className={styles.brandsSection}></section>
  ) : error ? (
    <div className={styles.errorContainer}>
      <h2>Error Loading Brands</h2>
      <p>Please try again later.</p>
    </div>
  ) : (
    brands && (
      <section className={styles.brandsSection}>
        <div className={`${styles.brandsSectionContainer} container`}>
          <div className={styles.brandSelector}>
            {sortedBrands &&
              sortedBrands.map((brand, index) => (
                <div className={styles.brand} key={index}>
                  <button
                    type="button"
                    onClick={() => handleChangeActiveBrand(brand.id)}
                    className={brand.id === activeBrand ? styles.active : ""}
                  >
                    <img
                      src={brand.acf.premium_brands.brand_logo.sizes.medium}
                      width={
                        brand.acf.premium_brands.brand_logo.sizes[
                          "medium-width"
                        ]
                      }
                      height={
                        brand.acf.premium_brands.brand_logo.sizes[
                          "medium-height"
                        ]
                      }
                      alt={brand.title.rendered + " logo"}
                    />
                  </button>
                </div>
              ))}
          </div>
          <div className={styles.mobileContent}>
            <div className={styles.mobileVideoContainer}>
              <img
                className={styles.gloveMobile}
                src={gloveMobile}
                alt="background of a gloved hand holding a mobile phone"
                height={425}
                width={431}
              />
              <div className={styles.videoPlayer}>
                {sortedBrands &&
                sortedBrands[currentBrandIndex]?.acf.premium_brands
                  .brand_video ? (
                  <video
                    muted
                    playsInline
                    autoPlay
                    controls
                    src={
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .brand_video
                    }
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
            <div className={`${styles.mobileBrandContent}`}>
              {sortedBrands && (
                <div>
                  <img
                    className={styles.brandLogo}
                    src={
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .brand_logo.sizes.medium
                    }
                    height={
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .brand_logo.sizes["medium-height"]
                    }
                    width={
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .brand_logo.sizes["medium-width"]
                    }
                    alt={
                      sortedBrands[currentBrandIndex]?.title.rendered + " logo"
                    }
                  />
                  <p>
                    {
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .brand_information
                    }
                  </p>
                  <a
                    href={
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .shop_link.url
                    }
                    target={
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .shop_link.target
                    }
                    className={styles.shopLink}
                  >
                    {
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .shop_link.title
                    }
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.largeContent}>
          <CSSTransition
            nodeRef={textContainerRef}
            timeout={500}
            classNames={{
              appear: styles.largeTextAppear,
              appearActive: styles.largeTextAppearActive,
              appearDone: styles.largeTextAppearDone,
              enter: styles.largeTextEnter,
              enterActive: styles.largeTextEnterActive,
              enterDone: styles.largeTextEnterDone,
              exit: styles.largeTextExit,
              exitActive: styles.largeTextExitActive,
              exitDone: styles.largeTextExitDone,
            }}
            in={videoPlayerIn}
          >
            <div className={styles.brandContent} ref={textContainerRef}>
              {sortedBrands && (
                <div>
                  <img
                    className={styles.brandLogo}
                    src={
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .brand_logo.sizes.medium
                    }
                    height={
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .brand_logo.sizes["medium-height"]
                    }
                    width={
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .brand_logo.sizes["medium-width"]
                    }
                    alt={
                      sortedBrands[currentBrandIndex]?.title.rendered + " logo"
                    }
                  />
                  <p>
                    {
                      sortedBrands[currentBrandIndex]?.acf.premium_brands
                        .brand_information
                    }
                  </p>
                  {sortedBrands[currentBrandIndex]?.acf.premium_brands
                    .shop_link && (
                    <a
                      href={
                        sortedBrands[currentBrandIndex]?.acf.premium_brands
                          .shop_link.url
                      }
                      target={
                        sortedBrands[currentBrandIndex]?.acf.premium_brands
                          .shop_link.target
                      }
                      className={styles.shopLink}
                    >
                      {
                        sortedBrands[currentBrandIndex]?.acf.premium_brands
                          .shop_link.title
                      }
                    </a>
                  )}
                </div>
              )}
            </div>
          </CSSTransition>
          <CSSTransition
            nodeRef={videoContainerRef}
            timeout={500}
            classNames={{
              appear: styles.largeVideoAppear,
              appearActive: styles.largeVideoAppearActive,
              appearDone: styles.largeVideoAppearDone,
              enter: styles.largeVideoEnter,
              enterActive: styles.largeVideoEnterActive,
              enterDone: styles.largeVideoEnterDone,
              exit: styles.largeVideoExit,
              exitActive: styles.largeVideoExitActive,
              exitDone: styles.largeVideoExitDone,
            }}
            in={videoPlayerIn}
          >
            <div className={styles.largeVideoContainer} ref={videoContainerRef}>
              <img
                className={styles.gloveMedium}
                src={gloveMedium}
                alt="background of a gloved hand holding a mobile phone"
                width={956}
                height={527}
              />
              <img
                className={styles.gloveLarge}
                src={gloveLarge}
                alt="background of a gloved hand holding a mobile phone"
                width={956}
                height={527}
              />
              <div className={styles.largeVideoPlayer}>
                {sortedBrands &&
                sortedBrands[currentBrandIndex]?.acf.premium_brands
                  .brand_video ? (
                  <>
                    <video
                      ref={videoPlayerRef}
                      muted={videoMuted}
                      playsInline
                      autoPlay={true}
                      controls={false}
                      onPlay={() => setVideoPlay(true)}
                      onPause={() => setVideoPlay(false)}
                      controlsList="nodownload "
                      loop
                      src={
                        sortedBrands[currentBrandIndex]?.acf.premium_brands
                          .brand_video
                      }
                      className={styles.largeVideoPlayerElement}
                    />
                    <div className={styles.controlsContainer}>
                      <button type="button" onClick={togglePlay}>
                        {videoPlay ? (
                          <i className="fa-solid fa-pause"></i>
                        ) : (
                          <i className="fa-solid fa-play"></i>
                        )}
                      </button>

                      <button type="button" onClick={toggleMute}>
                        {videoMuted ? (
                          <i className="fa-solid fa-volume-high"></i>
                        ) : (
                          <i className="fa-solid fa-volume-xmark"></i>
                        )}
                      </button>

                      <button type="button" onClick={fullscreenClick}>
                        <i className="fa-solid fa-expand"></i>
                      </button>
                    </div>
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
          </CSSTransition>
        </div>
      </section>
    )
  );
};
export default BrandsSection;
